import { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Printer, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { ReportExecution, REPORT_TYPE_INFO } from '../../types';

interface ReportViewerProps {
  execution: ReportExecution | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ReportViewer({ execution, isOpen, onClose }: ReportViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  if (!execution) return null;

  const reportInfo = REPORT_TYPE_INFO[execution.reportType];
  const sanitizedHtml = execution.reportHtml
    ? DOMPurify.sanitize(execution.reportHtml, {
        ADD_TAGS: ['style'],
        ADD_ATTR: ['target'],
      })
    : '';

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleOpenInNewTab = () => {
    if (!execution.reportHtml) return;

    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(sanitizedHtml);
      newWindow.document.close();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-6xl p-0 gap-0 flex flex-col [&>button]:hidden" style={{ height: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted rounded-t-lg">
          <div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              {reportInfo?.name || execution.reportType} Report
            </DialogTitle>
            <DialogDescription className="sr-only">View generated report content with print and export options</DialogDescription>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span>{execution.clientName}</span>
              <span>•</span>
              <span>
                Generated {format(new Date(execution.startedAt), 'MMM d, yyyy h:mm a')}
              </span>
              {execution.triggeredBy === 'manual' && (
                <>
                  <span>•</span>
                  <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs">
                    Manual Run
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenInNewTab}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
              New Tab
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              title="Print or Save as PDF"
            >
              <Printer className="h-4 w-4" />
              Print / PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-hidden bg-muted">
          {execution.reportHtml ? (
            <iframe
              ref={iframeRef}
              srcDoc={sanitizedHtml}
              className="w-full h-full border-0"
              title="Report Content"
              sandbox="allow-same-origin allow-popups"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground">
                <p className="text-lg font-medium">No report content available</p>
                <p className="mt-1 text-sm">
                  The report HTML was not saved with this execution.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer with stats */}
        {execution.stats && Object.keys(execution.stats).length > 0 && (
          <div className="px-6 py-3 border-t border-border bg-muted rounded-b-lg">
            <div className="flex items-center gap-6 text-sm">
              <span className="text-muted-foreground">Quick Stats:</span>
              {Object.entries(execution.stats).slice(0, 5).map(([key, value]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">
                    {formatStatKey(key)}:
                  </span>
                  <span className="font-medium text-foreground">
                    {formatStatValue(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatStatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

function formatStatValue(value: string | number | boolean | undefined): string {
  if (value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (value >= 0 && value <= 100 && !Number.isInteger(value)) {
      return `${value.toFixed(1)}%`;
    }
    return value.toLocaleString();
  }
  return String(value);
}
